import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

const SECTORS = [
  {
    t: "Custody & Transfer",
    roles: "Custodian · Transfer Agent · Register",
    d: "Governed handoffs where an asset or entitlement moves between accountable parties. The register consumes a verified control decision — it is not asked to trust an opaque instruction.",
  },
  {
    t: "Health & Care",
    roles: "Clinician · Patient · Care Network",
    d: "Evidence remains with the clinician, patient or system of record. AI may draft from approved summary evidence only; it cannot access dates of birth, safeguarding notes or withheld media.",
  },
  {
    t: "Logistics & Carriage",
    roles: "Shipper · Carrier · Consignee",
    d: "Each release, handoff and exception is governed by mandate and scope, with a receipt that proves the carriage event without exposing the underlying shipment contents.",
  },
  {
    t: "Underwriting & Risk",
    roles: "Underwriter · Broker · Insured",
    d: "Authority gates over selected, purpose-bound evidence references. The underwriting decision stays with the authorised role; KATLAS attests the governed outcome.",
  },
  {
    t: "Membership & Entitlement",
    roles: "Member · Administrator · Scheme",
    d: "Claims, releases and delegations are bounded by declared policy, with proof of what was shared and what was withheld at each step.",
  },
  {
    t: "Registers & Tokens",
    roles: "Register · Adapter · Token",
    d: "A token or smart contract consumes the Control Decision Envelope before execution — proof of authority, policy and evidence conditions, with no raw evidence centralised.",
  },
];

export default function Sectors() {
  return (
    <div data-testid="sectors-page">
      <PageIntro
        index="/sectors"
        eyebrow="Sectors"
        titleLines={["One governance pattern.", "Many accountable", "handoffs."]}
        lead="Wherever a consequential action depends on who holds custody, who holds authority and what proves the outcome, the same KATLAS pattern applies. These are illustrative applications — not named deployments."
        meta={["Custody boundary", "Authority matrix", "Public-safe receipt"]}
      />

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 md:grid-cols-2 lg:grid-cols-3">
            {SECTORS.map((s) => (
              <RevealItem key={s.t} className="group flex flex-col bg-stone p-8 md:p-9">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
                  {s.roles}
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ink">{s.t}</h3>
                <div className="mt-4 h-px w-8 bg-ink/20 transition-all duration-500 group-hover:w-16 group-hover:bg-teal" />
                <p className="mt-5 font-sans text-sm leading-relaxed text-ink/70">{s.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal delay={0.2} className="mt-16 flex flex-col items-start justify-between gap-6 border border-ink/15 p-8 md:flex-row md:items-center md:p-12">
            <p className="max-w-xl font-serif text-xl leading-snug text-ink md:text-2xl">
              If your sector turns on a consequential handoff, we can model it as a
              governed journey.
            </p>
            <Link
              to="/company#contact"
              className="group inline-flex shrink-0 items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
            >
              Bring us a journey
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
